<template>
  <div class="page_content">
    <div v-if="loading">
      <div class="component_loader">
        <spinner :status="loading" />
      </div>
    </div>
    <div v-else>
      <div
        class="d-flex justify-content-between align-items-end flex-wrap mb-3"
      >
        <div class="asset-page-actions">
          <button class="site_btn btn_green mr-2" @click="newAsset">
            {{ $t('assets.new_asset') }}
          </button>
          <button
            type="button"
            class="site_btn btn_white"
            :disabled="exportLoading"
            @click="exportAssets"
          >
            {{
              exportLoading
                ? $t('assets.exporting_assets')
                : $t('assets.export_assets')
            }}
            <b-spinner small label="spinning" v-if="exportLoading" />
          </button>
        </div>
      </div>

      <div class="box asset-search-box mb-3">
        <div class="asset-search-grid">
          <div class="form__group mb-0">
            <label class="form__label2 bold mr-2">
              {{ $t('assets.search_assets') }}
            </label>
            <input
              v-model="searchQuery"
              type="text"
              class="form__input"
              :placeholder="$t('assets.search_assets_placeholder')"
            />
          </div>

          <div class="form__group mb-0">
            <label class="form__label2 bold">
              {{ $t('assets.category_name') }}
            </label>
            <multiselect
              name="asset_search_category"
              :placeholder="$t('assets.filter_by_category')"
              v-model="searchCategory"
              :options="searchCategoryOptions"
              label="name"
              track-by="value"
              :allow-empty="true"
              :show-labels="false"
            />
          </div>

          <div class="asset-search-action">
            <button
              type="button"
              class="site_btn btn_white"
              :disabled="!hasActiveSearchFilters"
              @click="clearSearchFilters"
            >
              {{ $t('assets.clear_filters') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="assetList.length === 0" class="box">
        <h5 class="table-title">{{ $t('assets.assets') }}</h5>
        <p>{{ $t('assets.no_data') }}</p>
      </div>

      <div v-if="showSearchResultsTable" class="box asset-group-box">
        <div class="asset-group-header">
          <h5 class="table-title mb-0">{{ $t('assets.search_results') }}</h5>
          <div class="asset-group-header-actions">
            <label class="asset-table-select-label mb-0">
              <span>{{ $t('assets.rows_per_page') }}</span>
              <select v-model.number="searchPerPage" class="asset-table-select">
                <option
                  v-for="option in tablePerPageOptions"
                  :key="`search-per-page-${option}`"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </label>
          </div>
        </div>

        <div v-if="searchLoading" class="component_loader py-4">
          <spinner :status="searchLoading" />
        </div>

        <template v-else-if="searchResultItems.length > 0">
          <table class="table table-striped asset-group-table">
            <thead>
              <tr>
                <th scope="col">{{ $t('assets.image') }}</th>
                <th scope="col">
                  {{ $t('assets.asset') }} | {{ $t('assets.assigned_to') }}
                </th>
                <th scope="col">{{ $t('assets.category_name') }}</th>
                <th scope="col">{{ $t('assets.status') }}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in searchResultItems" :key="`search-${item.id}`">
                <td>
                  <div v-if="item.file">
                    <img
                      :src="SERVER_URL + 'company-assets/' + item.file"
                      class="asset-image"
                    />
                  </div>
                </td>
                <td>
                  <p>
                    {{ item.title }} | {{ assignedTo(item.history) || '-' }}
                  </p>
                </td>
                <td>
                  <p>{{ getAssetCategoryName(item) }}</p>
                </td>
                <td>
                  <span
                    :class="[
                      'asset-status',
                      'asset-status-' + normalizedStatus(item.status)
                    ]"
                  >
                    {{ getStatusLabel(item.status) }}
                  </span>
                </td>
                <td>
                  <div class="d-flex justify-content-end asset-row-actions">
                    <div
                      class="mr-1 on-hover"
                      v-b-tooltip.hover
                      :title="$t('assets.asset_history')"
                      @click="showAssetHistory(item)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="1em"
                        viewBox="0 0 448 512"
                      >
                        <path
                          d="M448 80v48c0 44.2-100.3 80-224 80S0 172.2 0 128V80C0 35.8 100.3 0 224 0S448 35.8 448 80zM393.2 214.7c20.8-7.4 39.9-16.9 54.8-28.6V288c0 44.2-100.3 80-224 80S0 332.2 0 288V186.1c14.9 11.8 34 21.2 54.8 28.6C99.7 230.7 159.5 240 224 240s124.3-9.3 169.2-25.3zM0 346.1c14.9 11.8 34 21.2 54.8 28.6C99.7 390.7 159.5 400 224 400s124.3-9.3 169.2-25.3c20.8-7.4 39.9-16.9 54.8-28.6V432c0 44.2-100.3 80-224 80S0 476.2 0 432V346.1z"
                        />
                      </svg>
                    </div>
                    <div
                      class="mr-1 ml-1 on-hover"
                      v-b-tooltip.hover
                      :title="$t('assets.edit_asset')"
                      @click="editAsset(item)"
                    >
                      <svg
                        width="12px"
                        viewBox="0 0 12 12"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                      >
                        <desc>Created with Sketch.</desc>
                        <defs />
                        <g id="Page-1" stroke="none" stroke-width="1">
                          <g
                            id="Business-Recruitment-Konkurset"
                            transform="translate(-1277.000000, -962.000000)"
                            fill-rule="nonzero"
                          >
                            <g
                              id="Notification"
                              transform="translate(1272.000000, 957.000000)"
                            >
                              <g
                                id="pencil-edit-button"
                                transform="translate(5.000000, 5.000000)"
                              >
                                <path
                                  d="M7.46189638,2.0026454 L9.90296784,4.45564876 L3.72394191,10.6649354 L1.28425447,8.21193199 L7.46189638,2.0026454 Z M11.7552689,1.41103724 L10.666645,0.31708196 C10.2459294,-0.105693987 9.56277427,-0.105693987 9.14062932,0.31708196 L8.09783645,1.36498172 L10.5389079,3.81800788 L11.7552689,2.59568996 C12.081577,2.26776101 12.081577,1.73894338 11.7552689,1.41103724 Z M0.00679292588,11.6598257 C-0.0376314435,11.8607372 0.142879426,12.0407641 0.342834466,11.9919042 L3.06299896,11.329138 L0.623311511,8.8761346 L0.00679292588,11.6598257 Z"
                                  id="Shape"
                                />
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                    </div>
                    <div
                      class="ml-1 on-hover"
                      v-b-tooltip.hover
                      :title="$t('assets.delete_asset')"
                      @click="deleteAsset(item)"
                    >
                      <svg
                        width="12px"
                        viewBox="0 0 14 19"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                      >
                        <desc>Created with Sketch.</desc>
                        <defs />
                        <g id="Page-1" stroke="none" stroke-width="1">
                          <g
                            id="Profile-Experience"
                            transform="translate(-1402.000000, -1117.000000)"
                            fill-rule="nonzero"
                          >
                            <g
                              id="Notification-Copy-2"
                              transform="translate(1395.000000, 1113.000000)"
                            >
                              <g
                                id="garbage"
                                transform="translate(7.000000, 4.000000)"
                              >
                                <path
                                  d="M12.75505,2.14285714 L9.45,2.14285714 L9.45,1.07142857 C9.45,0.480714286 8.9789,0 8.4,0 L5.6,0 C5.0211,0 4.55,0.480714286 4.55,1.07142857 L4.55,2.14285714 L1.24495,2.14285714 C0.5586,2.14285714 0,2.71285714 0,3.41321429 L0,5 L0.7,5 L13.3,5 L14,5 L14,3.41321429 C14,2.71285714 13.4414,2.14285714 12.75505,2.14285714 Z M8.75,2.14285714 L5.25,2.14285714 L5.25,1.07142857 C5.25,0.874285714 5.40715,0.714285714 5.6,0.714285714 L8.4,0.714285714 C8.59285,0.714285714 8.75,0.874285714 8.75,1.07142857 L8.75,2.14285714 Z"
                                  id="Shape"
                                />
                                <path
                                  d="M1.00033333,17.6898108 C1.00033333,18.4121892 1.558,19 2.24333333,19 L11.757,19 C12.4426667,19 13,18.4121892 13,17.6898108 L13,6 L1,6 L1,17.6898108 L1.00033333,17.6898108 Z"
                                  id="Shape"
                                />
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <b-pagination
            v-if="searchResults.total > searchResults.per_page"
            align="center"
            @change="loadSearchPage"
            :total-rows="searchResults.total"
            v-model="searchPage"
            :per-page="searchResults.per_page"
          />
        </template>

        <p v-else class="mb-0">{{ $t('assets.no_search_results') }}</p>
      </div>

      <div
        v-if="!showSearchResultsTable && unassignedAssets.length > 0"
        class="box asset-group-box"
      >
        <div class="asset-group-header">
          <h5 class="table-title mb-0">{{ $t('assets.available_assets') }}</h5>
          <div class="asset-group-header-actions">
            <label class="asset-table-select-label mb-0">
              <span>{{ $t('assets.rows_per_page') }}</span>
              <select
                v-model.number="availablePerPage"
                class="asset-table-select"
              >
                <option
                  v-for="option in tablePerPageOptions"
                  :key="`available-per-page-${option}`"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </label>
          </div>
        </div>
        <table class="table table-striped asset-group-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('assets.image') }}</th>
              <th scope="col">{{ $t('assets.asset') }}</th>
              <th scope="col">{{ $t('assets.category_name') }}</th>
              <th scope="col">{{ $t('assets.status') }}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in paginatedUnassignedAssets"
              :key="`unassigned-${item.id}`"
            >
              <td>
                <div v-if="item.file">
                  <img
                    :src="SERVER_URL + 'company-assets/' + item.file"
                    class="asset-image"
                  />
                </div>
              </td>
              <td>
                <p>{{ item.title }}</p>
              </td>
              <td>
                <p>{{ getAssetCategoryName(item) }}</p>
              </td>
              <td>
                <span
                  :class="[
                    'asset-status',
                    'asset-status-' + normalizedStatus(item.status)
                  ]"
                >
                  {{ getStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <div class="d-flex justify-content-end asset-row-actions">
                  <div
                    class="mr-1 on-hover"
                    v-b-tooltip.hover
                    :title="$t('assets.asset_history')"
                    @click="showAssetHistory(item)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="1em"
                      viewBox="0 0 448 512"
                    >
                      <path
                        d="M448 80v48c0 44.2-100.3 80-224 80S0 172.2 0 128V80C0 35.8 100.3 0 224 0S448 35.8 448 80zM393.2 214.7c20.8-7.4 39.9-16.9 54.8-28.6V288c0 44.2-100.3 80-224 80S0 332.2 0 288V186.1c14.9 11.8 34 21.2 54.8 28.6C99.7 230.7 159.5 240 224 240s124.3-9.3 169.2-25.3zM0 346.1c14.9 11.8 34 21.2 54.8 28.6C99.7 390.7 159.5 400 224 400s124.3-9.3 169.2-25.3c20.8-7.4 39.9-16.9 54.8-28.6V432c0 44.2-100.3 80-224 80S0 476.2 0 432V346.1z"
                      />
                    </svg>
                  </div>
                  <div
                    class="mr-1 ml-1 on-hover"
                    v-b-tooltip.hover
                    :title="$t('assets.edit_asset')"
                    @click="editAsset(item)"
                  >
                    <svg
                      width="12px"
                      viewBox="0 0 12 12"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <desc>Created with Sketch.</desc>
                      <defs />
                      <g id="Page-1" stroke="none" stroke-width="1">
                        <g
                          id="Business-Recruitment-Konkurset"
                          transform="translate(-1277.000000, -962.000000)"
                          fill-rule="nonzero"
                        >
                          <g
                            id="Notification"
                            transform="translate(1272.000000, 957.000000)"
                          >
                            <g
                              id="pencil-edit-button"
                              transform="translate(5.000000, 5.000000)"
                            >
                              <path
                                d="M7.46189638,2.0026454 L9.90296784,4.45564876 L3.72394191,10.6649354 L1.28425447,8.21193199 L7.46189638,2.0026454 Z M11.7552689,1.41103724 L10.666645,0.31708196 C10.2459294,-0.105693987 9.56277427,-0.105693987 9.14062932,0.31708196 L8.09783645,1.36498172 L10.5389079,3.81800788 L11.7552689,2.59568996 C12.081577,2.26776101 12.081577,1.73894338 11.7552689,1.41103724 Z M0.00679292588,11.6598257 C-0.0376314435,11.8607372 0.142879426,12.0407641 0.342834466,11.9919042 L3.06299896,11.329138 L0.623311511,8.8761346 L0.00679292588,11.6598257 Z"
                                id="Shape"
                              />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </div>
                  <div
                    class="ml-1 on-hover"
                    v-b-tooltip.hover
                    :title="$t('assets.delete_asset')"
                    @click="deleteAsset(item)"
                  >
                    <svg
                      width="12px"
                      viewBox="0 0 14 19"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <desc>Created with Sketch.</desc>
                      <defs />
                      <g id="Page-1" stroke="none" stroke-width="1">
                        <g
                          id="Profile-Experience"
                          transform="translate(-1402.000000, -1117.000000)"
                          fill-rule="nonzero"
                        >
                          <g
                            id="Notification-Copy-2"
                            transform="translate(1395.000000, 1113.000000)"
                          >
                            <g
                              id="garbage"
                              transform="translate(7.000000, 4.000000)"
                            >
                              <path
                                d="M12.75505,2.14285714 L9.45,2.14285714 L9.45,1.07142857 C9.45,0.480714286 8.9789,0 8.4,0 L5.6,0 C5.0211,0 4.55,0.480714286 4.55,1.07142857 L4.55,2.14285714 L1.24495,2.14285714 C0.5586,2.14285714 0,2.71285714 0,3.41321429 L0,5 L0.7,5 L13.3,5 L14,5 L14,3.41321429 C14,2.71285714 13.4414,2.14285714 12.75505,2.14285714 Z M8.75,2.14285714 L5.25,2.14285714 L5.25,1.07142857 C5.25,0.874285714 5.40715,0.714285714 5.6,0.714285714 L8.4,0.714285714 C8.59285,0.714285714 8.75,0.874285714 8.75,1.07142857 L8.75,2.14285714 Z"
                                id="Shape"
                              />
                              <path
                                d="M1.00033333,17.6898108 C1.00033333,18.4121892 1.558,19 2.24333333,19 L11.757,19 C12.4426667,19 13,18.4121892 13,17.6898108 L13,6 L1,6 L1,17.6898108 L1.00033333,17.6898108 Z"
                                id="Shape"
                              />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <b-pagination
          v-if="unassignedAssets.length > availablePerPage"
          align="center"
          :total-rows="unassignedAssets.length"
          v-model="availablePage"
          :per-page="availablePerPage"
        />
      </div>

      <div
        v-if="!showSearchResultsTable && groupedAssignedAssets.length > 0"
        class="box asset-group-box"
      >
        <div class="asset-group-header">
          <h5 class="table-title mb-0">{{ $t('assets.assigned_assets') }}</h5>
          <div class="asset-group-header-actions">
            <label class="asset-table-select-label mb-0">
              <span>{{ $t('assets.rows_per_page') }}</span>
              <select
                v-model.number="assignedPerPage"
                class="asset-table-select"
              >
                <option
                  v-for="option in tablePerPageOptions"
                  :key="`assigned-per-page-${option}`"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </label>
          </div>
        </div>
        <table class="table table-striped asset-group-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('assets.image') }}</th>
              <th scope="col">
                {{ $t('assets.asset') }} | {{ $t('assets.assigned_to') }}
              </th>
              <th scope="col">{{ $t('assets.category_name') }}</th>
              <th scope="col">{{ $t('assets.status') }}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in paginatedGroupedAssignedAssets"
              :key="`assigned-${item.id}`"
            >
              <td>
                <div v-if="item.file">
                  <img
                    :src="SERVER_URL + 'company-assets/' + item.file"
                    class="asset-image"
                  />
                </div>
              </td>
              <td>
                <p>{{ item.title }} | {{ assignedTo(item.history) || '-' }}</p>
              </td>
              <td>
                <p>{{ getAssetCategoryName(item) }}</p>
              </td>
              <td>
                <span
                  :class="[
                    'asset-status',
                    'asset-status-' + normalizedStatus(item.status)
                  ]"
                >
                  {{ getStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <div class="d-flex justify-content-end asset-row-actions">
                  <div
                    class="mr-1 on-hover"
                    v-b-tooltip.hover
                    :title="$t('assets.asset_history')"
                    @click="showAssetHistory(item)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="1em"
                      viewBox="0 0 448 512"
                    >
                      <path
                        d="M448 80v48c0 44.2-100.3 80-224 80S0 172.2 0 128V80C0 35.8 100.3 0 224 0S448 35.8 448 80zM393.2 214.7c20.8-7.4 39.9-16.9 54.8-28.6V288c0 44.2-100.3 80-224 80S0 332.2 0 288V186.1c14.9 11.8 34 21.2 54.8 28.6C99.7 230.7 159.5 240 224 240s124.3-9.3 169.2-25.3zM0 346.1c14.9 11.8 34 21.2 54.8 28.6C99.7 390.7 159.5 400 224 400s124.3-9.3 169.2-25.3c20.8-7.4 39.9-16.9 54.8-28.6V432c0 44.2-100.3 80-224 80S0 476.2 0 432V346.1z"
                      />
                    </svg>
                  </div>
                  <div
                    class="mr-1 ml-1 on-hover"
                    v-b-tooltip.hover
                    :title="$t('assets.edit_asset')"
                    @click="editAsset(item)"
                  >
                    <svg
                      width="12px"
                      viewBox="0 0 12 12"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <desc>Created with Sketch.</desc>
                      <defs />
                      <g id="Page-1" stroke="none" stroke-width="1">
                        <g
                          id="Business-Recruitment-Konkurset"
                          transform="translate(-1277.000000, -962.000000)"
                          fill-rule="nonzero"
                        >
                          <g
                            id="Notification"
                            transform="translate(1272.000000, 957.000000)"
                          >
                            <g
                              id="pencil-edit-button"
                              transform="translate(5.000000, 5.000000)"
                            >
                              <path
                                d="M7.46189638,2.0026454 L9.90296784,4.45564876 L3.72394191,10.6649354 L1.28425447,8.21193199 L7.46189638,2.0026454 Z M11.7552689,1.41103724 L10.666645,0.31708196 C10.2459294,-0.105693987 9.56277427,-0.105693987 9.14062932,0.31708196 L8.09783645,1.36498172 L10.5389079,3.81800788 L11.7552689,2.59568996 C12.081577,2.26776101 12.081577,1.73894338 11.7552689,1.41103724 Z M0.00679292588,11.6598257 C-0.0376314435,11.8607372 0.142879426,12.0407641 0.342834466,11.9919042 L3.06299896,11.329138 L0.623311511,8.8761346 L0.00679292588,11.6598257 Z"
                                id="Shape"
                              />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </div>
                  <div
                    class="ml-1 on-hover"
                    v-b-tooltip.hover
                    :title="$t('assets.delete_asset')"
                    @click="deleteAsset(item)"
                  >
                    <svg
                      width="12px"
                      viewBox="0 0 14 19"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <desc>Created with Sketch.</desc>
                      <defs />
                      <g id="Page-1" stroke="none" stroke-width="1">
                        <g
                          id="Profile-Experience"
                          transform="translate(-1402.000000, -1117.000000)"
                          fill-rule="nonzero"
                        >
                          <g
                            id="Notification-Copy-2"
                            transform="translate(1395.000000, 1113.000000)"
                          >
                            <g
                              id="garbage"
                              transform="translate(7.000000, 4.000000)"
                            >
                              <path
                                d="M12.75505,2.14285714 L9.45,2.14285714 L9.45,1.07142857 C9.45,0.480714286 8.9789,0 8.4,0 L5.6,0 C5.0211,0 4.55,0.480714286 4.55,1.07142857 L4.55,2.14285714 L1.24495,2.14285714 C0.5586,2.14285714 0,2.71285714 0,3.41321429 L0,5 L0.7,5 L13.3,5 L14,5 L14,3.41321429 C14,2.71285714 13.4414,2.14285714 12.75505,2.14285714 Z M8.75,2.14285714 L5.25,2.14285714 L5.25,1.07142857 C5.25,0.874285714 5.40715,0.714285714 5.6,0.714285714 L8.4,0.714285714 C8.59285,0.714285714 8.75,0.874285714 8.75,1.07142857 L8.75,2.14285714 Z"
                                id="Shape"
                              />
                              <path
                                d="M1.00033333,17.6898108 C1.00033333,18.4121892 1.558,19 2.24333333,19 L11.757,19 C12.4426667,19 13,18.4121892 13,17.6898108 L13,6 L1,6 L1,17.6898108 L1.00033333,17.6898108 Z"
                                id="Shape"
                              />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <b-pagination
          v-if="groupedAssignedAssets.length > assignedPerPage"
          align="center"
          :total-rows="groupedAssignedAssets.length"
          v-model="assignedPage"
          :per-page="assignedPerPage"
        />
      </div>
    </div>

    <div class="profile_edit_modals" v-if="add_edit_modal">
      <div class="modal_item">
        <div class="boxi">
          <div class="close_modal" @click="closeModal">
            <img src="/images/close.svg" width="20px" />
          </div>
          <div class="container">
            <form class="form-type-1" @submit.prevent="addEditAssetFormHandler">
              <div class="textinBox">
                <h4>Register new asset</h4>
                <p>
                  Here you can register all your company assets and then manage
                  them easily!
                </p>
              </div>
              <div class="row">
                <div class="col-md-3">
                  <div class="form__group">
                    <label class="form__label2 bold"
                      >{{ $t('inputs.title')
                      }}<span class="required">(*)</span></label
                    >
                    <input
                      class="form__input"
                      required
                      type="text"
                      :placeholder="$t('inputs.title')"
                      v-model="title"
                      name="title"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group">
                    <label class="form__label2 bold"
                      >{{ $t('inputs.serial_number') }}<span class="required"
                    /></label>
                    <input
                      class="form__input"
                      type="text"
                      :placeholder="$t('inputs.serial_number')"
                      v-model="serial_number"
                      name="serial_number"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group">
                    <label class="form__label2 bold">{{
                      $t('assets.select_asset_category')
                    }}</label>
                    <multiselect
                      class="mt-2"
                      name="assets_options"
                      :placeholder="$t('assets.select_asset_category')"
                      v-model="category"
                      :options="categoryOptions"
                      label="name"
                      track-by="value"
                      :allow-empty="false"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group">
                    <label class="form__label2 bold">{{
                      $t('assets.identifier_input_mode')
                    }}</label>
                    <select v-model="identifier_mode" class="form__input">
                      <option value="auto">
                        {{ $t('assets.identifier_mode_auto') }}
                      </option>
                      <option value="manual">
                        {{ $t('assets.identifier_mode_manual') }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group">
                    <label class="form__label2 bold"
                      >{{ $t('assets.unique_identifier')
                      }}<span
                        v-if="identifier_mode === 'manual'"
                        class="required"
                        >(*)</span
                      ></label
                    >
                    <input
                      class="form__input"
                      :readonly="identifier_mode === 'auto'"
                      :disabled="identifier_mode === 'auto'"
                      :placeholder="
                        identifier_mode === 'auto'
                          ? $t('assets.identifier_auto_placeholder')
                          : $t('assets.unique_identifier')
                      "
                      v-model="unique_identifier"
                      name="unique_identifier"
                    />
                    <small
                      v-if="identifier_mode === 'auto'"
                      class="text-muted d-block mt-1"
                    >
                      {{ $t('assets.identifier_auto_help') }}
                    </small>
                    <small
                      v-if="identifierError"
                      class="text-danger d-block mt-1"
                    >
                      {{ identifierError }}
                    </small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group">
                    <label class="form__label2 bold">{{
                      $t('assets.status')
                    }}</label>
                    <select v-model="status" class="form__input">
                      <option
                        v-for="option in statusOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group">
                    <label for="from_date" class="form__label2 bold">{{
                      $t('assets.image')
                    }}</label>
                    <input
                      type="file"
                      name="file"
                      id="file"
                      ref="file"
                      accept="image/*"
                      v-on:change="handleFile()"
                      style="display: none"
                    />
                    <input
                      type="button"
                      class="btn btninputfile"
                      :value="file ? $t('assets.change') : $t('assets.add')"
                      onclick="document.getElementById('file').click();"
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <vue-editor
                    :editorToolbar="customToolbar"
                    v-model="description"
                    name="description"
                  />
                </div>
              </div>
              <div class="row mt-5">
                <div class="col-md-12">
                  <div class="text-center">
                    <button
                      type="submit"
                      class="site_btn btn_blue"
                      :disabled="submit_loading"
                    >
                      {{ editing ? $t('btn.update') : $t('btn.save') }}
                      <b-spinner label="spinning" v-if="submit_loading" />
                    </button>
                    <button type="button" @click="closeModal" class="ml-2">
                      {{ $t('btn.cancelbtn') }}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="profile_edit_modals" v-if="history_modal">
      <div class="modal_item">
        <div class="boxi">
          <div class="close_modal" @click="closeModal">
            <img src="/images/close.svg" width="20px" />
          </div>
          <div class="container">
            <div class="textinBox">
              <h4>Asset History</h4>
              <p>
                {{ selected_asset.title }} - {{ selected_asset.serial_number }}
              </p>
              <p v-if="selected_asset.id">
                {{ $t('assets.unique_identifier') }}:
                {{ selected_asset.unique_identifier }}
              </p>
              <p v-if="selected_asset.id">
                {{ $t('assets.current_status') }}:
                <span
                  :class="[
                    'asset-status',
                    'asset-status-' + normalizedStatus(selected_asset.status)
                  ]"
                >
                  {{ getStatusLabel(selected_asset.status) }}
                </span>
              </p>
            </div>
            <div v-if="selected_asset.id" class="asset-qr-card mb-4">
              <div class="asset-qr-preview">
                <img
                  v-if="qrCodeDataUrl"
                  :src="qrCodeDataUrl"
                  :alt="selected_asset.unique_identifier"
                  class="asset-qr-image"
                />
                <div v-else class="asset-qr-placeholder">
                  {{ $t('assets.qr_code_not_available') }}
                </div>
              </div>
              <div class="asset-qr-content">
                <h5 class="asset-qr-title">{{ $t('assets.qr_code') }}</h5>
                <p class="asset-qr-copy">
                  {{ $t('assets.qr_code_description') }}
                </p>
                <p class="asset-qr-value">
                  {{ selected_asset.unique_identifier }}
                </p>
                <div class="asset-qr-actions">
                  <button
                    type="button"
                    class="site_btn btn_blue"
                    :disabled="!qrCodeDataUrl"
                    @click="downloadQrCode"
                  >
                    {{ $t('assets.download_qr_code') }}
                  </button>
                  <button
                    type="button"
                    class="site_btn btn_white"
                    :disabled="!qrCodeDataUrl"
                    @click="printQrCode"
                  >
                    {{ $t('assets.print_qr_code') }}
                  </button>
                </div>
              </div>
            </div>
            <div v-if="selectedAssetHistory.length > 0">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th scope="col">{{ $t('assets.assigned_to') }}</th>
                    <th scope="col">{{ $t('assets.assignment_date') }}</th>
                    <th scope="col">{{ $t('assets.return_date') }}</th>
                    <th scope="col">{{ $t('assets.note') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in selectedAssetHistory" :key="item.id">
                    <td>
                      <p>
                        {{ item.employee.name }} {{ item.employee.surname }}
                      </p>
                    </td>
                    <td>
                      <p>{{ item.asset_assignment_date }}</p>
                    </td>
                    <td>
                      <p>{{ item.asset_return_date }}</p>
                    </td>
                    <td>
                      <p>{{ item.note }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else>
              <p>There is no history for this asset</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import * as XLSX from 'xlsx'
import { VueEditor } from 'vue2-editor'
import Multiselect from 'vue-multiselect'

export default {
  components: {
    VueEditor,
    Multiselect
  },

  inject: {
    $validator: '$validator'
  },

  data() {
    return {
      customToolbar: [
        ['bold'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ]
      ],

      loading: false,
      submit_loading: false,

      add_edit_modal: false,
      title: '',
      serial_number: '',
      unique_identifier: '',
      identifier_mode: 'auto',
      identifierError: '',
      description: '',
      status: 'available',
      qrCodeDataUrl: '',

      selected_asset: '',
      editing: false,
      history_modal: false,
      category: '',
      categoryOptions: [],
      file: null,

      searchQuery: '',
      searchCategory: null,
      searchCategoryOptions: [],
      searchResults: {
        data: [],
        total: 0,
        current_page: 1,
        per_page: 20,
        last_page: 1
      },
      searchLoading: false,
      exportLoading: false,
      searchDebounceTimeout: null,
      tablePerPageOptions: [5, 10, 20, 40],
      availablePage: 1,
      availablePerPage: 20,
      assignedPage: 1,
      assignedPerPage: 20,
      searchPage: 1,
      searchPerPage: 20
    }
  },

  computed: {
    assets: function () {
      if (this.$store.getters['company_data/assets/get']) {
        return this.$store.getters['company_data/assets/get']
      }

      return { all: [] }
    },

    assetList: function () {
      if (this.assets && Array.isArray(this.assets.all)) {
        return this.assets.all
      }

      const freeAssets =
        this.assets && Array.isArray(this.assets.free) ? this.assets.free : []
      const assignedAssets =
        this.assets && Array.isArray(this.assets.assigned)
          ? this.assets.assigned
          : []

      return [...freeAssets, ...assignedAssets]
    },

    availableAssets: function () {
      return this.assetList.filter((item) => {
        return this.normalizedStatus(item.status) === 'available'
      })
    },

    unassignedAssets: function () {
      return this.assetList.filter(
        (item) => !this.getActiveAssignment(item.history)
      )
    },

    assignedAssets: function () {
      return this.assetList.filter((item) => {
        return this.normalizedStatus(item.status) === 'assigned'
      })
    },

    groupedAssignedAssets: function () {
      return this.assetList.filter((item) =>
        Boolean(this.getActiveAssignment(item.history))
      )
    },

    paginatedUnassignedAssets: function () {
      return this.getPaginatedItems(
        this.unassignedAssets,
        this.availablePage,
        this.availablePerPage
      )
    },

    paginatedGroupedAssignedAssets: function () {
      return this.getPaginatedItems(
        this.groupedAssignedAssets,
        this.assignedPage,
        this.assignedPerPage
      )
    },

    selectedSearchCategoryId: function () {
      return this.searchCategory && this.searchCategory.value
        ? this.searchCategory.value
        : ''
    },

    hasActiveSearchFilters: function () {
      return Boolean(this.searchQuery.trim() || this.selectedSearchCategoryId)
    },

    showSearchResultsTable: function () {
      return this.hasActiveSearchFilters || this.searchLoading
    },

    searchResultItems: function () {
      if (this.searchResults && Array.isArray(this.searchResults.data)) {
        return this.searchResults.data
      }

      return []
    },

    selectedAssetHistory: function () {
      if (this.selected_asset && Array.isArray(this.selected_asset.history)) {
        return this.selected_asset.history
      }

      return []
    },

    statusOptions: function () {
      return [
        { value: 'available', label: this.$t('assets.available') },
        { value: 'in_maintenance', label: this.$t('assets.in_maintenance') },
        { value: 'retired', label: this.$t('assets.retired') }
      ]
    }
  },

  watch: {
    assets: function () {
      this.normalizeClientPages()
    },

    searchQuery: function () {
      this.searchPage = 1
      this.scheduleSearchRefresh()
    },

    searchCategory: function () {
      this.searchPage = 1
      this.runAssetSearch()
    },

    availablePerPage: function () {
      this.availablePage = 1
      this.normalizeClientPages()
    },

    assignedPerPage: function () {
      this.assignedPage = 1
      this.normalizeClientPages()
    },

    searchPerPage: function () {
      this.searchPage = 1

      if (this.hasActiveSearchFilters) {
        this.runAssetSearch()
      }
    }
  },

  methods: {
    getPaginatedItems: function (items, currentPage, perPage) {
      const normalizedPerPage = Number(perPage) || 20
      const normalizedPage = Number(currentPage) || 1
      const startIndex = (normalizedPage - 1) * normalizedPerPage

      return items.slice(startIndex, startIndex + normalizedPerPage)
    },

    getMaxPage: function (totalItems, perPage) {
      return Math.max(1, Math.ceil(totalItems / perPage))
    },

    normalizeClientPages: function () {
      this.availablePage = Math.min(
        this.availablePage,
        this.getMaxPage(this.unassignedAssets.length, this.availablePerPage)
      )
      this.assignedPage = Math.min(
        this.assignedPage,
        this.getMaxPage(this.groupedAssignedAssets.length, this.assignedPerPage)
      )
    },

    emptySearchResults: function () {
      return {
        data: [],
        total: 0,
        current_page: 1,
        per_page: this.searchPerPage,
        last_page: 1
      }
    },

    normalizeSearchResults: function (response) {
      const responseData = response && response.data ? response.data : {}

      return {
        data: Array.isArray(responseData.data) ? responseData.data : [],
        total: Number(responseData.total) || 0,
        current_page: Number(responseData.current_page) || 1,
        per_page: Number(responseData.per_page) || this.searchPerPage,
        last_page: Number(responseData.last_page) || 1
      }
    },

    normalizeSearchCategoryOptions: function (response) {
      const responseData =
        response && Array.isArray(response.data) ? response.data : []

      return responseData.map((item) => ({
        value: item.value || item.id,
        name: item.name
      }))
    },

    normalizeAssetListResponse: function (response) {
      if (response && response.data && Array.isArray(response.data.data)) {
        return response.data.data
      }

      return []
    },

    getCurrentCompanyId: function () {
      return this.$getCurrentCompanyIdForAdmin()
    },

    getCurrentSearchFilters: function () {
      return {
        companyId: this.getCurrentCompanyId(),
        search: this.searchQuery.trim(),
        category_id: this.selectedSearchCategoryId
      }
    },

    fetchSearchCategoryOptions: function () {
      return this.$store
        .dispatch('company_data/assets/get_company_asset_search_categories', {
          ...this.getCurrentSearchFilters()
        })
        .then((response) => {
          if (response && response.status === 200) {
            this.searchCategoryOptions =
              this.normalizeSearchCategoryOptions(response)
          }
        })
    },

    runAssetSearch: function () {
      if (!this.hasActiveSearchFilters) {
        this.searchLoading = false
        this.searchResults = this.emptySearchResults()
        return Promise.resolve()
      }

      this.searchLoading = true

      return this.$store
        .dispatch('company_data/assets/search_company_assets', {
          ...this.getCurrentSearchFilters(),
          page: this.searchPage,
          per_page: this.searchPerPage
        })
        .then((response) => {
          if (response && response.status === 200) {
            this.searchResults = this.normalizeSearchResults(response)
            this.searchPage = this.searchResults.current_page
          }

          this.searchLoading = false
        })
        .catch(() => {
          this.searchLoading = false
        })
    },

    scheduleSearchRefresh: function () {
      clearTimeout(this.searchDebounceTimeout)
      this.searchDebounceTimeout = setTimeout(() => {
        this.fetchSearchCategoryOptions()
        this.runAssetSearch()
      }, 300)
    },

    clearSearchFilters: function () {
      clearTimeout(this.searchDebounceTimeout)
      this.searchQuery = ''
      this.searchCategory = null
      this.searchCategoryOptions = []
      this.searchPage = 1
      this.searchResults = this.emptySearchResults()
      this.fetchSearchCategoryOptions()
    },

    loadSearchPage: function (page) {
      this.searchPage = page
      this.runAssetSearch()
    },

    showAssetHistory: function (item) {
      this.selected_asset = item
      this.history_modal = true
      this.generateQrCode(item.unique_identifier)
    },

    assignedTo: function (history) {
      if (!Array.isArray(history)) {
        return ''
      }

      const item = history.find((entry) => entry.status === 1)

      if (!item || !item.employee) {
        return ''
      }

      return `${item.employee.name} ${item.employee.surname}`
    },

    getActiveAssignment: function (history) {
      if (!Array.isArray(history)) {
        return null
      }

      return history.find((entry) => entry.status === 1) || null
    },

    stripHtml: function (value) {
      if (!value) {
        return ''
      }

      const container = document.createElement('div')
      container.innerHTML = value
      return (container.textContent || container.innerText || '').trim()
    },

    getAssetCategoryName: function (item) {
      if (item && item.category_name) {
        return item.category_name
      }

      if (item && item.category && item.category.name) {
        return item.category.name
      }

      if (item && item.category_id) {
        return `${this.$t('assets.deleted_category')} #${item.category_id}`
      }

      return '-'
    },

    resolveAssetImageLink: function (item) {
      const imageLink = item && item.image_link ? item.image_link : ''

      if (!imageLink && item && item.file) {
        return `${this.SERVER_URL}company-assets/${item.file}`
      }

      if (!imageLink) {
        return ''
      }

      if (/^https?:\/\//i.test(imageLink)) {
        return imageLink
      }

      return `${this.SERVER_URL}${imageLink}`
    },

    getExportColumns: function () {
      return [
        { key: 'image_link', label: 'image link' },
        { key: 'asset_name', label: 'asset name' },
        {
          key: 'asset_unique_identifier',
          label: 'asset unique identifier'
        },
        { key: 'asset_category_name', label: 'asset category name' },
        { key: 'assigned_to', label: 'assigned to' },
        { key: 'status', label: 'status' }
      ]
    },

    buildExportRows: function (assets) {
      return assets.map((item) => {
        return {
          image_link: this.resolveAssetImageLink(item),
          asset_name: item.title || '',
          asset_unique_identifier: item.unique_identifier || '',
          asset_category_name: this.getAssetCategoryName(item),
          assigned_to: item.assigned_to || this.assignedTo(item.history) || '-',
          status: this.getStatusLabel(item.status)
        }
      })
    },

    createHiddenExportTable: function (rows) {
      const table = document.createElement('table')
      table.setAttribute('aria-hidden', 'true')
      table.style.position = 'absolute'
      table.style.left = '-99999px'
      table.style.top = '0'

      const columns = this.getExportColumns()
      const thead = document.createElement('thead')
      const headerRow = document.createElement('tr')

      columns.forEach((column) => {
        const th = document.createElement('th')
        th.textContent = column.label
        headerRow.appendChild(th)
      })

      thead.appendChild(headerRow)
      table.appendChild(thead)

      const tbody = document.createElement('tbody')

      rows.forEach((row) => {
        const tr = document.createElement('tr')

        columns.forEach((column) => {
          const td = document.createElement('td')
          td.textContent = row[column.key] || ''
          tr.appendChild(td)
        })

        tbody.appendChild(tr)
      })

      table.appendChild(tbody)
      document.body.appendChild(table)

      return table
    },

    downloadExportTable: function (table) {
      const workbook = XLSX.utils.table_to_book(table, { sheet: 'Assets' })
      const workbookData = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })
      const blob = new Blob([workbookData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = this.hasActiveSearchFilters
        ? 'filtered-assets.xlsx'
        : 'assets.xlsx'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 0)
    },

    destroyHiddenExportTable: function (table) {
      if (table && table.parentNode) {
        table.parentNode.removeChild(table)
      }
    },

    exportAssets: function () {
      this.exportLoading = true

      return this.$store
        .dispatch('company_data/assets/get_company_assets_list', {
          ...this.getCurrentSearchFilters()
        })
        .then((response) => {
          if (!response || response.status !== 200) {
            throw new Error('EXPORT_REQUEST_FAILED')
          }

          const assets = this.normalizeAssetListResponse(response)

          if (assets.length === 0) {
            this.$toast.error(this.$t('assets.no_assets_to_export'), 'Error', {
              timeut: 3000
            })
            return
          }

          const exportRows = this.buildExportRows(assets)
          const hiddenTable = this.createHiddenExportTable(exportRows)

          try {
            this.downloadExportTable(hiddenTable)
          } finally {
            this.destroyHiddenExportTable(hiddenTable)
          }
        })
        .catch(() => {
          this.$toast.error(this.$t('alerts.smthwrong'), 'Error', {
            timeut: 3000
          })
        })
        .finally(() => {
          this.exportLoading = false
        })
    },

    newAsset: function () {
      this.add_edit_modal = true
    },

    closeModal: function () {
      this.add_edit_modal = false
      this.submit_loading = false
      this.title = ''
      this.serial_number = ''
      this.unique_identifier = ''
      this.identifier_mode = 'auto'
      this.identifierError = ''
      this.description = ''
      this.status = 'available'
      this.qrCodeDataUrl = ''
      this.selected_asset = ''
      this.category = ''
      this.editing = false
      this.history_modal = false
      this.file = null
    },

    handleFile() {
      this.file = this.$refs.file.files[0]
    },

    generateQrCode: function (value) {
      this.qrCodeDataUrl = ''

      if (!value) {
        return
      }

      QRCode.toDataURL(value, {
        errorCorrectionLevel: 'H',
        margin: 2,
        width: 240
      })
        .then((dataUrl) => {
          this.qrCodeDataUrl = dataUrl
        })
        .catch(() => {
          this.$toast.error(this.$t('alerts.smthwrong'), 'Error', {
            timeut: 3000
          })
        })
    },

    downloadQrCode: function () {
      if (!this.qrCodeDataUrl || !this.selected_asset.unique_identifier) {
        return
      }

      const link = document.createElement('a')
      link.href = this.qrCodeDataUrl
      link.download = `${this.selected_asset.unique_identifier}.png`
      link.click()
    },

    printQrCode: function () {
      if (!this.qrCodeDataUrl || !this.selected_asset.unique_identifier) {
        return
      }

      const printWindow = window.open('', '_blank', 'width=600,height=800')

      if (!printWindow) {
        return
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>${this.selected_asset.unique_identifier}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
              }
              .qr-print {
                text-align: center;
              }
              .qr-print img {
                width: 280px;
                height: 280px;
              }
              .qr-print p {
                font-size: 18px;
                margin-top: 16px;
              }
            </style>
          </head>
          <body>
            <div class="qr-print">
              <img src="${this.qrCodeDataUrl}" alt="${this.selected_asset.unique_identifier}" />
              <p>${this.selected_asset.unique_identifier}</p>
            </div>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    },

    addEditAssetFormHandler: function () {
      this.submit_loading = true
      this.identifierError = ''
      const data = new FormData()
      data.append('title', this.title)
      data.append('serial_number', this.serial_number)
      data.append('identifier_mode', this.identifier_mode)
      if (this.identifier_mode === 'manual') {
        data.append('unique_identifier', this.unique_identifier)
      }
      data.append('description', this.description)
      data.append('status', this.status)
      data.append('category_id', this.category.value ? this.category.value : '')
      if (this.file) {
        data.append('file', this.file)
      }
      if (this.editing) {
        data.append('id', this.selected_asset.id)
        this.$store
          .dispatch('company_data/assets/update_asset', data)
          .then((response) => {
            this.handleAssetSaveResponse(response)
          })
      } else {
        this.$store
          .dispatch('company_data/assets/add_new_asset', data)
          .then((response) => {
            this.handleAssetSaveResponse(response)
          })
      }
    },

    handleAssetSaveResponse: function (response) {
      if (response && response.status === 200) {
        this.closeModal()
        this.fetchData()
        return
      }

      const duplicateIdentifierError =
        response &&
        response.data &&
        response.data.errors &&
        response.data.errors.unique_identifier &&
        response.data.errors.unique_identifier.length > 0
          ? response.data.errors.unique_identifier[0]
          : ''

      if (duplicateIdentifierError) {
        this.identifierError = duplicateIdentifierError
        this.$toast.error(duplicateIdentifierError, 'Error', { timeut: 3000 })
      } else {
        this.$toast.error(this.$t('alerts.smthwrong'), 'Error', {
          timeut: 3000
        })
      }

      this.submit_loading = false
    },

    fetchData: function () {
      this.loading = true
      this.$store
        .dispatch('company_data/assets/get_company_assets')
        .then(() => {
          this.$store
            .dispatch(
              'company_data/asset_categories/get_company_asset_categories'
            )
            .then((response) => {
              this.categoryOptions = []
              for (const category of response.data) {
                this.categoryOptions.push({
                  name: category.name,
                  value: category.id
                })
              }
              this.fetchSearchCategoryOptions()
              if (this.hasActiveSearchFilters) {
                this.runAssetSearch()
              }
              this.normalizeClientPages()
              this.loading = false
            })
        })
    },

    editAsset: function (item) {
      this.selected_asset = item
      this.title = item.title
      this.serial_number = item.serial_number
      this.unique_identifier = item.unique_identifier
      this.identifier_mode = 'manual'
      this.identifierError = ''
      this.description = item.description
      this.status = this.normalizedStatus(item.status)
      this.editing = true
      this.add_edit_modal = true
      if (item.category_id) {
        const selectedCategory = this.categoryOptions.find(
          (category) => category.value === item.category_id
        )

        if (selectedCategory) {
          this.category = {
            value: item.category_id,
            name: selectedCategory.name
          }
        }
      }
    },

    deleteAsset: function (item) {
      const data = {
        id: item.id
      }

      this.$store
        .dispatch('company_data/assets/delete_asset', data)
        .then(() => {
          this.fetchData()
        })
    },

    normalizedStatus: function (status) {
      return status || 'available'
    },

    formatExportDate: function (value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleString()
    },

    getStatusLabel: function (status) {
      const labels = {
        available: this.$t('assets.available'),
        assigned: this.$t('assets.assigned'),
        in_maintenance: this.$t('assets.in_maintenance'),
        retired: this.$t('assets.retired')
      }

      return labels[this.normalizedStatus(status)]
    }
  },

  created() {
    this.fetchData()
  }
}
</script>

<style lang="scss">
.table {
  p {
    margin: 0;
    padding: 0;
    font-size: 13px;
  }

  th {
    font-weight: 500 !important;
    font-size: 15px;
    color: #58595b;
  }
}

.table-title {
  color: #58595b;
  font-weight: bold;
  font-size: 15px;
}

.asset-image {
  width: 50px !important;
}

.asset-group-box {
  padding: 18px 28px 26px;
}

.asset-group-box .table-title {
  margin-bottom: 22px;
  font-size: 23px;
  font-weight: 700;
  color: #555b65;
}

.asset-group-box + .asset-group-box {
  margin-top: 18px;
}

.asset-group-table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0 8px;
}

.asset-group-table thead th {
  padding: 10px 18px 18px;
  border-top: 0;
  border-bottom: 1px solid #d8dee6;
  font-size: 17px;
  font-weight: 700 !important;
  color: #5f646d;
}

.asset-group-table tbody tr {
  background: #f1f1f1;
}

.asset-group-table tbody td {
  padding: 18px;
  vertical-align: middle;
  border-top: 0;
}

.asset-group-table tbody p {
  font-size: 16px;
  color: #6a6e75;
}

.asset-group-table tbody td:first-child {
  width: 140px;
}

.asset-group-table tbody td:first-child,
.asset-group-table thead th:first-child {
  padding-left: 18px;
}

.asset-group-table th:last-child,
.asset-group-table td:last-child {
  width: 110px;
  padding-right: 22px;
}

.asset-row-actions {
  min-width: 72px;
  gap: 10px;
}

.asset-row-actions svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 767px) {
  .asset-group-box {
    padding: 16px;
  }

  .asset-group-box .table-title {
    font-size: 20px;
    margin-bottom: 16px;
  }

  .asset-group-table {
    border-spacing: 0 6px;
  }

  .asset-group-table thead th,
  .asset-group-table tbody td {
    padding: 14px 12px;
  }
}

.asset-group-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 22px;
}

.asset-group-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.asset-search-box {
  padding: 20px 24px;
}

.asset-search-grid {
  display: grid;
  grid-template-columns: minmax(240px, 1.4fr) minmax(220px, 1fr) auto;
  gap: 16px;
  align-items: end;
}

.asset-search-action {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  min-height: 100%;
}

.asset-table-select-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #5f646d;
  font-size: 14px;
  font-weight: 600;
}

.asset-table-select {
  min-width: 92px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d8dee6;
  border-radius: 10px;
  background: #fff;
  color: #3f4752;
}

.asset-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.asset-status-available {
  background: #e7f7ef;
  color: #1f7a45;
}

.asset-status-assigned {
  background: #e8f0fe;
  color: #1d4ed8;
}

.asset-status-in_maintenance {
  background: #fff4db;
  color: #a16207;
}

.asset-status-retired {
  background: #f3f4f6;
  color: #4b5563;
}

.asset-qr-card {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%);
}

.asset-qr-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 260px;
  min-height: 260px;
  padding: 12px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #e5e7eb;
}

.asset-qr-image {
  width: 240px;
  height: 240px;
}

.asset-qr-placeholder {
  color: #6b7280;
  text-align: center;
  font-size: 14px;
}

.asset-qr-content {
  flex: 1;
  min-width: 240px;
}

.asset-qr-title {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.asset-qr-copy {
  margin-bottom: 12px;
  color: #4b5563;
}

.asset-qr-value {
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  word-break: break-word;
}

.asset-qr-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

@media (max-width: 767px) {
  .asset-search-box {
    padding: 16px;
  }

  .asset-search-grid {
    grid-template-columns: 1fr;
  }

  .asset-search-action,
  .asset-group-header-actions {
    justify-content: stretch;
  }

  .asset-table-select-label {
    width: 100%;
    justify-content: space-between;
  }

  .asset-table-select {
    flex: 1;
  }

  .asset-qr-card {
    padding: 16px;
  }

  .asset-qr-preview {
    width: 100%;
  }
}
</style>
